import { queryGraphQL, mutateGraphQL } from 'common/util/query';
import { addApplicationMessage, addApplicationMessages } from 'common/actions/applicationMessagesActions';
import { toQuery, toMutation, nodeWithArgs, node } from 'common/util/queryHelper';
import { pick, isNil, isEmpty } from 'lodash';

export const ADMIN_LOAD_DOCUMENT_TYPES = 'ADMIN_LOAD_DOCUMENT_TYPES';
export const ADMIN_LOAD_DOCUMENT_TYPES_LIST = 'ADMIN_LOAD_DOCUMENT_TYPES_LIST';
export const ADMIN_CREATE_DOCUMENT_TYPE = 'ADMIN_CREATE_DOCUMENT_TYPE';
export const ADMIN_UPDATE_DOCUMENT_TYPE = 'ADMIN_UPDATE_DOCUMENT_TYPE';
export const ADMIN_DELETE_DOCUMENT_TYPE = 'ADMIN_DELETE_DOCUMENT_TYPE';
export const ADMIN_INITIALIZE_UPDATE_DOCUMENT_TYPE_SCENE = 'ADMIN_INITIALIZE_UPDATE_DOCUMENT_TYPE_SCENE';
export const ADMIN_CREATE_DOCUMENT_TYPE_DESCRIPTOR_LINK = 'ADMIN_CREATE_DOCUMENT_TYPE_DESCRIPTOR_LINK';
export const ADMIN_DELETE_DOCUMENT_TYPE_DESCRIPTOR_LINK = 'ADMIN_DELETE_DOCUMENT_TYPE_DESCRIPTOR_LINK';
export const ADMIN_UPDATE_DOCUMENT_TYPE_DESCRIPTOR_LINK = 'ADMIN_UPDATE_DOCUMENT_TYPE_DESCRIPTOR_LINK';

const fieldsToSave = ['name', 'displayName', 'isActive', 'institutionId', 'description', 'autonameTemplate', 'manualMapping'];

const manualMappingFields = node('manualMapping', [
    node('eTMF', [
        'entityName',
        'entityValue',
    ])
]);

export function loadDocumentTypesList(institutionId) {
    return dispatch => {
        const query = toQuery(node('ComplionAdmin', [
            nodeWithArgs('institution', {
                id: institutionId
            }, [
                node('documentTypes', [
                    'id',
                    'name',
                    'displayName',
                    'isActive',
                    'description',
                    'institutionId'
                ])
            ])
        ]));

        return queryGraphQL(query)
            .then(response => {
                if (response.errors) {
                    dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
                }
                else {
                    const {
                        ComplionAdmin: {
                            institution: {
                                documentTypes
                            }
                        }
                    } = response.data;
                    dispatch({
                        type: ADMIN_LOAD_DOCUMENT_TYPES_LIST,
                        payload: {
                            documentTypes: documentTypes || []
                        }
                    });
                }
            });
    };
}

// initializeUpdateDocumentTypeScene is only used in Complion Admin although it currently sits inside of Institutional Admin.
// It should be moved to the appropriate place along with any related code that uses it.
export function initializeUpdateDocumentTypeScene(institutionId, documentTypeId) {
    const queries = [
        node('ComplionAdmin', [
            nodeWithArgs('institution', {
                id: institutionId
            }, [
                nodeWithArgs('documentTypes', {
                    id: documentTypeId
                }, [
                    'id',
                    'name',
                    'displayName',
                    'isActive',
                    'description',
                    'institutionId',
                    'autonameTemplate',
                    manualMappingFields,
                    node('descriptors', [
                        'id',
                        'name',
                        'displayName',
                        'dataType',
                        'description',
                        'isActive',
                        'isActiveForDocumentType',
                        'isRequiredForDocumentType',
                        'displayOrder',
                        'dataSourceId'
                    ]),
                ]),
                node('dataSources', [
                    'id',
                    'name',
                    'description'
                ]),
                node('descriptors', [
                    'id', 'name', 'displayName', 'description', 'dataType', 'isActive'
                ]),
            ]),
        ]),
    ];

    const query = toQuery(queries);

    return dispatch => queryGraphQL(query).then(response => {
        if (response.errors) {
            dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
        }
        else {
            const {
                ComplionAdmin: {
                    institution: {
                        documentTypes: [documentType],
                        dataSources,
                        descriptors,
                    },
                },
            } = response.data;
            dispatch({
                type: ADMIN_INITIALIZE_UPDATE_DOCUMENT_TYPE_SCENE,
                payload: {
                    documentType,
                    descriptors,
                    dataSources,
                }
            });
        }
    });
}

export function loadDocumentTypes(institutionId) {
    return dispatch => {
        const query = toQuery(
            node('ComplionAdmin', [
                nodeWithArgs('institution', {
                    id: institutionId
                }, [
                    node('documentTypes', [
                        'id',
                        'name',
                        'displayName',
                        'isActive',
                        'description',
                        'autonameTemplate',
                        manualMappingFields,
                        node('descriptors', [
                            'id',
                            'name',
                            'displayName',
                            'dataType',
                            'description',
                            'isActive',
                            'isActiveForDocumentType',
                            'isRequiredForDocumentType',
                            'displayOrder',
                            'dataSourceId'
                        ])
                    ])
                ])
            ])
        );


        return queryGraphQL(query).then(results => {
            const {
                ComplionAdmin: {
                    institution: {
                        documentTypes
                    }
                }
            } = results.data;
            dispatch({
                type: ADMIN_LOAD_DOCUMENT_TYPES,
                payload: {
                    documentTypes: documentTypes || [],
                    institutionId: institutionId
                }
            });
        });
    };
}

export function createDocumentType(documentType, institutionId) {
    documentType.institutionId = institutionId;
    return dispatch => {
        const mutation = toMutation(
            nodeWithArgs('addDocumentType', {
                documentType: pick(documentType, fieldsToSave)
            }, [
                'id',
                'name',
                'displayName',
                'isActive',
                'description',
                'institutionId'
            ])
        );

        return mutateGraphQL(mutation).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
            } else {
                dispatch({
                    type: ADMIN_CREATE_DOCUMENT_TYPE,
                    payload: {
                        documentType: response.data.addDocumentType,
                        institutionId: institutionId
                    }
                });
            }
        });
    };
}

export function updateDocumentType(documentType) {
    return dispatch => {
        const mutation = toMutation(
            nodeWithArgs('updateDocumentType', {
                input: pick(documentType, ['id', 'displayName', 'isActive', 'description', 'autonameTemplate', 'manualMapping',])
            }, [
                node('documentType', [
                    'id',
                    'name',
                    'displayName',
                    'isActive',
                    'description',
                    'autonameTemplate',
                    manualMappingFields,
                    node('descriptors', [
                        'id',
                        'name',
                        'displayName',
                        'dataType',
                        'description',
                        'isActive',
                        'isActiveForDocumentType',
                        'isRequiredForDocumentType',
                        'displayOrder',
                        'dataSourceId'
                    ])
                ]),
            ])
        );

        return mutateGraphQL(mutation).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
            }
            else {
                dispatch({
                    type: ADMIN_UPDATE_DOCUMENT_TYPE,
                    payload: {
                        documentType: response.data.updateDocumentType.documentType
                    }
                });
            }
        });
    };
}

export function deleteDocumentType(documentTypeId, institutionId) {
    return dispatch => {
        const mutation = toMutation(
            nodeWithArgs('deleteDocumentType', {
                id: documentTypeId
            })
        );

        return mutateGraphQL(mutation).then(results => {
            //results.data.deleteDocumentType is boolean
            if (results.data.deleteDocumentType) {
                dispatch({
                    type: ADMIN_DELETE_DOCUMENT_TYPE,
                    payload: {
                        institutionId: institutionId,
                        documentTypeId
                    }
                });
            }
        });
    };
}

export function createDocumentTypeDescriptorLink(documentTypeId, descriptorId) {
    return dispatch => {
        const mutation = toMutation(nodeWithArgs('createDocumentTypeDescriptorLink', {
            input: { documentTypeId, descriptorId }
        }, [
            node('documentType', [
                'id',
                'name',
                'displayName',
                'isActive',
                'description',
                'institutionId',
                'autonameTemplate',
                manualMappingFields,
                node('descriptors', [
                    'id',
                    'name',
                    'displayName',
                    'dataType',
                    'description',
                    'isActive',
                    'isActiveForDocumentType',
                    'isRequiredForDocumentType',
                    'displayOrder',
                    'dataSourceId'
                ])
            ])
        ]));

        return mutateGraphQL(mutation).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
            }
            else {
                dispatch({
                    type: ADMIN_CREATE_DOCUMENT_TYPE_DESCRIPTOR_LINK,
                    payload: response.data.createDocumentTypeDescriptorLink
                });
            }
        });
    };
}

export function deleteDocumentTypeDescriptorLink(documentTypeId, descriptorId) {
    return dispatch => {
        const mutation = toMutation(nodeWithArgs('deleteDocumentTypeDescriptorLink', {
            input: { documentTypeId, descriptorId }
        }, [
            node('documentType', [
                'id',
                'name',
                'displayName',
                'isActive',
                'description',
                'institutionId',
                'autonameTemplate',
                manualMappingFields,
                node('descriptors', [
                    'id',
                    'name',
                    'displayName',
                    'dataType',
                    'description',
                    'isActive',
                    'isActiveForDocumentType',
                    'isRequiredForDocumentType',
                    'displayOrder',
                    'dataSourceId'
                ])
            ])]));

        return mutateGraphQL(mutation).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
            }
            else {
                dispatch({
                    type: ADMIN_DELETE_DOCUMENT_TYPE_DESCRIPTOR_LINK,
                    payload: response.data.deleteDocumentTypeDescriptorLink
                });
            }
        });
    };
}

export function updateDocumentTypeDescriptorLink(documentTypeId, descriptorId, values) {
    const { isActive, isRequired, displayOrder, dataSourceId } = values;
    return dispatch => {
        const input = {
            documentTypeId,
            descriptorId,
            isActive: !!isActive,
            isRequired: !!isRequired,
            displayOrder
        };
        if (!isNil(dataSourceId) && !isEmpty(dataSourceId)) {
            input.dataSourceId = dataSourceId;
        } else {
            input.removeDataSource = true;
        }
        const mutation = toMutation(nodeWithArgs('updateDocumentTypeDescriptorLink', {
            input
        }, [
            node('documentType', [
                'id',
                'name',
                'displayName',
                'isActive',
                'description',
                'institutionId',
                'autonameTemplate',
                manualMappingFields,
                node('descriptors', [
                    'id',
                    'name',
                    'displayName',
                    'dataType',
                    'description',
                    'isActive',
                    'isActiveForDocumentType',
                    'isRequiredForDocumentType',
                    'displayOrder',
                    'dataSourceId'
                ])
            ])]));

        return mutateGraphQL(mutation).then(response => {
            if (response.errors) {
                dispatch(addApplicationMessages('error', response.errors.map(e => e.message)));
            }
            else {
                dispatch({
                    type: ADMIN_UPDATE_DOCUMENT_TYPE_DESCRIPTOR_LINK,
                    payload: response.data.updateDocumentTypeDescriptorLink
                });
                dispatch(addApplicationMessage('success', 'The linked descriptor update was successful.'));
            }
        });
    };
}
